import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  Switch,
  CircularProgress,
  Collapse,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  CalendarToday as CalendarTodayIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import DateRangeCalendar, { DateRangeButton } from "../calendar/Calendar";
import {
  format,
  isWithinInterval,
  parseISO,
  endOfDay,
  startOfDay,
} from "date-fns";
import NewVersionModal from "./NewVersionModal";

const ContractTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [groupedTemplates, setGroupedTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sortType, setSortType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  // 새 버전 생성 모달 상태
  const [newVersionModalOpen, setNewVersionModalOpen] = useState(false);

  // 날짜 필터 상태
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [dateFilterActive, setDateFilterActive] = useState(false);

  const navigate = useNavigate();

  // 그리드 컬럼 설정 (일관된 너비)
  const gridColumns = "32px 1fr 90px 80px 120px 120px 40px";
  const minTableWidth = 1050;

  // 템플릿을 그룹화하는 함수
  const groupTemplates = (templates) => {
    const groups = {};

    templates.forEach((template) => {
      const rootId = template.parentTemplateId || template.id;

      if (!groups[rootId]) {
        groups[rootId] = {
          rootId,
          latest: null,
          versions: [],
        };
      }

      if (template.isLatest) {
        groups[rootId].latest = template;
      } else {
        groups[rootId].versions.push(template);
      }
    });

    Object.values(groups).forEach((group) => {
      group.versions.sort((a, b) => b.version - a.version);
    });

    const sortedGroups = Object.values(groups)
      .filter((group) => group.latest)
      .sort((a, b) => {
        const dateA = a.latest?.createdAt
          ? new Date(a.latest.createdAt)
          : new Date(0);
        const dateB = b.latest?.createdAt
          ? new Date(b.latest.createdAt)
          : new Date(0);
        return dateB - dateA;
      });

    return sortedGroups;
  };

  // 템플릿 목록 조회
  const fetchTemplates = async (
    keyword = "",
    sort = "",
    status = "",
    startDate = null,
    endDate = null
  ) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (keyword) params.append("keyword", keyword);

      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await fetch(
        `https://contract.tirebank.kr/api/contract-pdf/templates${queryString}`
      );

      if (!response.ok) throw new Error("템플릿 목록 조회 실패");
      const data = await response.json();

      let processedData = [...data];

      if (sort === "latest") {
        processedData.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } else if (sort === "oldest") {
        processedData.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      }

      if (status === "사용중") {
        processedData = processedData.filter((template) => template.isActive);
      } else if (status === "미사용") {
        processedData = processedData.filter((template) => !template.isActive);
      }

      if (dateFilterActive && startDate && endDate) {
        try {
          const startDayStart = startOfDay(new Date(startDate));
          const endDayEnd = endOfDay(new Date(endDate));

          processedData = processedData.filter((template) => {
            if (!template.createdAt) return false;
            try {
              const templateDate = parseISO(template.createdAt);
              return isWithinInterval(templateDate, {
                start: startDayStart,
                end: endDayEnd,
              });
            } catch (error) {
              return false;
            }
          });
        } catch (error) {
          console.error("날짜 필터링 적용 중 오류:", error);
        }
      }

      setTemplates(processedData);
      setGroupedTemplates(groupTemplates(processedData));
    } catch (error) {
      console.error("템플릿 조회 중 오류:", error);
      alert("템플릿 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates(
      searchKeyword,
      sortType,
      statusFilter,
      dateFilterActive ? startDate : null,
      dateFilterActive ? endDate : null
    );
  }, [
    searchKeyword,
    sortType,
    statusFilter,
    dateFilterActive,
    startDate,
    endDate,
  ]);

  const handleSearch = (event) => {
    setSearchKeyword(event.target.value);
  };

  const handleSortChange = (event) => {
    setSortType(event.target.value);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleOpenDateDialog = () => setDateDialogOpen(true);
  const handleCloseDateDialog = () => setDateDialogOpen(false);
  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };
  const handleApplyDateFilter = () => {
    if (startDate && endDate) {
      setDateFilterActive(true);
      handleCloseDateDialog();
    }
  };
  const handleResetDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setDateFilterActive(false);
    handleCloseDateDialog();
  };

  const getDateRangeText = () => {
    if (!dateFilterActive || !startDate || !endDate) return "전체";
    try {
      return `${format(new Date(startDate), "yy-MM-dd")} ~ ${format(
        new Date(endDate),
        "yy-MM-dd"
      )}`;
    } catch {
      return "전체";
    }
  };

  const handleMenuClick = (event, template) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = (keepSelectedTemplate = false) => {
    setAnchorEl(null);
    if (!keepSelectedTemplate) {
      setSelectedTemplate(null);
    }
  };

  const handleEditTemplate = () => {
    if (selectedTemplate) {
      navigate(`/edit-template/${selectedTemplate.id}`);
    }
    handleMenuClose();
  };

  const handleOpenNewVersionModal = () => {
    handleMenuClose(true);
    setNewVersionModalOpen(true);
  };

  const handleCloseNewVersionModal = () => {
    setNewVersionModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleNewVersionSuccess = () => {
    fetchTemplates(
      searchKeyword,
      sortType,
      statusFilter,
      dateFilterActive ? startDate : null,
      dateFilterActive ? endDate : null
    );
  };

  const handleToggleActive = async (e, template) => {
    e.stopPropagation();
    const newActiveStatus = !template.isActive;

    try {
      const response = await fetch(
        `https://contract.tirebank.kr/api/contract-pdf/templates/${template.id}/active?isActive=${newActiveStatus}`,
        { method: "PATCH" }
      );

      if (!response.ok) throw new Error("상태 변경 실패");

      fetchTemplates(
        searchKeyword,
        sortType,
        statusFilter,
        dateFilterActive ? startDate : null,
        dateFilterActive ? endDate : null
      );
    } catch (error) {
      console.error("템플릿 상태 변경 중 오류:", error);
      alert("템플릿 상태 변경에 실패했습니다.");
    }
  };

  const handleTemplatePreview = (templateId) => {
    navigate(`/contract-templates/${templateId}/preview`);
  };

  const toggleGroupExpand = (rootId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [rootId]: !prev[rootId],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString)
      .toLocaleDateString("ko-KR", {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(/\./g, ". ")
      .replace("시", ":")
      .replace("분", "");
  };

  // 템플릿 행 렌더링
  const renderTemplateRow = (template, hasVersions = false, isSubVersion = false) => (
    <Box
      key={template.id}
      sx={{
        display: "grid",
        gridTemplateColumns: gridColumns,
        alignItems: "center",
        p: 1.5,
        borderBottom: "1px solid #EEEEEE",
        backgroundColor: isSubVersion ? "#FAFAFA" : "white",
        "&:hover": { backgroundColor: isSubVersion ? "#F5F5F5" : "#F8F9FA" },
        minWidth: minTableWidth,
      }}
    >
      {/* 첫 번째 컬럼: 확장 아이콘 또는 들여쓰기 */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {hasVersions ? (
          <IconButton
            size="small"
            onClick={() => toggleGroupExpand(template.parentTemplateId || template.id)}
            sx={{ p: 0.5 }}
          >
            {expandedGroups[template.parentTemplateId || template.id] ? (
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        ) : isSubVersion ? (
          <Typography sx={{ color: "#999", fontSize: 14 }}>└</Typography>
        ) : null}
      </Box>

      {/* 제목 + 설명 영역 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          overflow: "hidden",
          "&:hover .template-title": { textDecoration: "underline" },
        }}
        onClick={() => handleTemplatePreview(template.id)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AttachFileIcon sx={{ color: "#3182F6", fontSize: 18, flexShrink: 0 }} />
          <Typography
            className="template-title"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {template.templateName}
          </Typography>
          <Chip
            label={`v${template.version}`}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              backgroundColor: template.isLatest ? "#E3F2FD" : "#F5F5F5",
              color: template.isLatest ? "#1976D2" : "#666",
              flexShrink: 0,
            }}
          />
          {template.isLatest && (
            <Chip
              label="최신"
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                backgroundColor: "#E8F5E9",
                color: "#2E7D32",
                flexShrink: 0,
              }}
            />
          )}
        </Box>
        {template.description && (
          <Typography
            sx={{
              fontSize: 11,
              color: "#999",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: 0.3,
              ml: 3,
            }}
            title={template.description}
          >
            {template.description}
          </Typography>
        )}
      </Box>

      {/* 상태 칩 */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Chip
          label={template.isActive ? "사용중" : "미사용"}
          size="small"
          sx={{
            backgroundColor: template.isActive ? "#E8F3FF" : "#F5F5F5",
            color: template.isActive ? "#3182F6" : "#666",
            height: 22,
            fontSize: 11,
          }}
        />
      </Box>

      {/* 활성/비활성 토글 */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Switch
          checked={template.isActive}
          onChange={(e) => handleToggleActive(e, template)}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#3182F6" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#3182F6" },
          }}
        />
      </Box>

      {/* 작성일 */}
      <Typography sx={{ fontSize: 12, color: "#666", textAlign: "center" }}>
        {formatDate(template.createdAt)}
      </Typography>

      {/* 수정일 */}
      <Typography sx={{ fontSize: 12, color: "#666", textAlign: "center" }}>
        {template.updatedAt ? formatDate(template.updatedAt) : "-"}
      </Typography>

      {/* 더보기 메뉴 */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <IconButton size="small" onClick={(e) => handleMenuClick(e, template)}>
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#F8F8FE",
        minHeight: "100vh",
      }}
    >
      {/* 상단 헤더 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#3A3A3A" }}>
          계약서 템플릿
        </Typography>
      </Box>

      {/* 검색 및 필터 영역 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        {/* 검색어 입력 */}
        <Box sx={{ flex: { xs: "1 1 100%", md: 1 }, minWidth: { md: 200 } }}>
          <Typography variant="caption" sx={{ mb: 1, color: "#666", display: "block" }}>
            검색어
          </Typography>
          <TextField
            placeholder="템플릿 이름"
            size="small"
            sx={{
              width: "100%",
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#E0E0E0" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E" }} />
                </InputAdornment>
              ),
            }}
            value={searchKeyword}
            onChange={handleSearch}
          />
        </Box>

        {/* 정렬 필터 */}
        <Box sx={{ flex: { xs: "1 1 45%", md: "0 0 auto" } }}>
          <Typography variant="caption" sx={{ mb: 1, color: "#666", display: "block" }}>
            정렬
          </Typography>
          <FormControl size="small" sx={{ width: { xs: "100%", md: 120 }, backgroundColor: "white" }}>
            <Select
              displayEmpty
              value={sortType}
              onChange={handleSortChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" } }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="latest">최신순</MenuItem>
              <MenuItem value="oldest">오래된순</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* 상태 필터 */}
        <Box sx={{ flex: { xs: "1 1 45%", md: "0 0 auto" } }}>
          <Typography variant="caption" sx={{ mb: 1, color: "#666", display: "block" }}>
            상태
          </Typography>
          <FormControl size="small" sx={{ width: { xs: "100%", md: 120 }, backgroundColor: "white" }}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={handleStatusChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" } }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="사용중">사용중</MenuItem>
              <MenuItem value="미사용">미사용</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* 기간 선택 */}
        <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 auto" } }}>
          <Typography variant="caption" sx={{ mb: 1, color: "#666", display: "block" }}>
            기간
          </Typography>
          <DateRangeButton
            startDate={startDate}
            endDate={endDate}
            isActive={dateFilterActive}
            onClick={handleOpenDateDialog}
            getDateRangeText={getDateRangeText}
            buttonProps={{
              startIcon: <CalendarTodayIcon />,
              endIcon: <KeyboardArrowDownIcon />,
            }}
          />
        </Box>

        <DateRangeCalendar
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          open={dateDialogOpen}
          onClose={handleCloseDateDialog}
          onApply={handleApplyDateFilter}
          onReset={handleResetDateFilter}
        />
      </Box>

      {/* 목록 영역 */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 1,
          border: "1px solid #EEEEEE",
          overflowX: "auto",
        }}
      >
        {/* 목록 헤더 */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            p: 1.5,
            borderBottom: "1px solid #EEEEEE",
            backgroundColor: "#F8F9FA",
            minWidth: minTableWidth,
          }}
        >
          <Box /> {/* 확장 아이콘 컬럼 */}
          <Typography variant="subtitle2" sx={{ color: "#666", fontSize: 13 }}>
            제목
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "#666", textAlign: "center", fontSize: 13 }}>
            상태
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "#666", textAlign: "center", fontSize: 13 }}>
            활성화
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "#666", textAlign: "center", fontSize: 13 }}>
            작성일
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "#666", textAlign: "center", fontSize: 13 }}>
            수정일
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "#666", textAlign: "center", fontSize: 13 }}>
            더보기
          </Typography>
        </Box>

        {/* 검색 결과 요약 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1.5,
            borderBottom: "1px solid #EEEEEE",
            minWidth: minTableWidth,
          }}
        >
          <Typography variant="body2" sx={{ color: "#666", fontSize: 13 }}>
            전체 {groupedTemplates.length}개 템플릿 ({templates.length}개 버전)
          </Typography>
          {(searchKeyword || statusFilter || dateFilterActive) && (
            <Typography variant="body2" sx={{ color: "#1976d2", fontSize: 13 }}>
              {searchKeyword && `검색어: "${searchKeyword}" `}
              {statusFilter && `상태: ${statusFilter} `}
              {dateFilterActive && `기간: ${getDateRangeText()}`}
            </Typography>
          )}
        </Box>

        {/* 템플릿 목록 */}
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
            <Typography sx={{ mt: 1 }}>로딩중...</Typography>
          </Box>
        ) : groupedTemplates.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">저장된 템플릿이 없습니다.</Typography>
          </Box>
        ) : (
          groupedTemplates.map((group) => (
            <Box key={group.rootId}>
              {/* 최신 버전 행 */}
              {renderTemplateRow(group.latest, group.versions.length > 0, false)}

              {/* 이전 버전들 (Collapse) */}
              {group.versions.length > 0 && (
                <Collapse in={expandedGroups[group.rootId]} timeout="auto" unmountOnExit>
                  {group.versions.map((version) => renderTemplateRow(version, false, true))}
                </Collapse>
              )}
            </Box>
          ))
        )}
      </Box>

      {/* 더보기 메뉴 */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditTemplate}>수정</MenuItem>
        {selectedTemplate?.isLatest && (
          <MenuItem onClick={handleOpenNewVersionModal}>새 버전 생성</MenuItem>
        )}
      </Menu>

      {/* 새 버전 생성 모달 */}
      <NewVersionModal
        open={newVersionModalOpen}
        onClose={handleCloseNewVersionModal}
        template={selectedTemplate}
        onSuccess={handleNewVersionSuccess}
      />
    </Box>
  );
};

export default ContractTemplate;
